import { FiEdit, FiX } from "react-icons/fi";
import { Button } from "reactstrap";
import { Link } from "react-router-dom";
import { VscPassFilled } from "react-icons/vsc";
import { MdPendingActions } from "react-icons/md";
import { TiTick } from "react-icons/ti";
import { TiDelete } from "react-icons/ti";
import { absTimeDifference } from "utils/timeDifference";
import { leaveTypes } from "pages/StaffApplication/constants";

export const columns = [
	{
		id: "personnelId",
		Header: "Personnel ID",
		accessor: "personnelId",
		sortable: true,
		filterable: true,
	},
	{
		id: "username",
		Header: "Username",
		accessor: "username",
		sortable: true,
		filterable: true,
	},
	{
		id: "role",
		Header: "Role",
		accessor: (row) => row?.role?.name,
		sortable: true,
		filterable: true,
	},
	{
		id: "email",
		Header: "Email",
		accessor: (row) => row?.email || "No email",
		sortable: true,
		filterable: true,
	},
	{
		id: "isActive",
		Header: "Active",
		accessor: (row) => (row.active ? <div className="text-success">Active</div> : <div className="text-danger">Deactivated</div>),
	},
	{
		id: "actions",
		Header: "actions",
		accessor: "actions",
		sortable: false,
		filterable: false,
	},
];

export const actions = (row) => (
	<div className="actions-right">
		<Link to={`/staff/${row.id}`}>
			<Button color="warning" size="sm" className="btn-icon btn-link edit">
				<FiEdit size={20} />
			</Button>
		</Link>
	</div>
);

export const leaveTableColumns = [
	{
		id: "status",
		Header: "Processed",
		accessor: (row) => (
			<div className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}>
				{row.status === "Processed" ? <VscPassFilled size={20} /> : <MdPendingActions size={20} />}
			</div>
		),
		sortable: true,
		filterable: true,
	},
	{
		id: "date",
		Header: "Date",
		accessor: (row) => new Date(row.date).toDateString(),
		sortable: true,
		filterable: true,
	},
	{
		id: "leaveType",
		Header: "leaveType",
		accessor: "type",
		filter: (rows, columnIds, filterValue) => {
			if (filterValue === "all") return rows;
			return rows.filter((row) => row.values.leaveType === filterValue);
		},
		Filter: ({ setFilter, column }) => (
			<select className="form-control" onChange={(event) => setFilter(column.id, event.target.value)} onClick={(e) => e.stopPropagation()}>
				<option value="all">All</option>
				{leaveTypes.map(({ value, label }) => (
					<option value={value} key={value}>
						{label}
					</option>
				))}
			</select>
		),
		filterable: true,
	},
	{
		id: "approved",
		Header: "Approved",
		accessor: "approved",
		Cell: ({ value }) => (
			<div className={`${value ? "text-success" : "text-danger"}`}>{value ? <TiTick size={20} /> : <TiDelete size={20} />}</div>
		),
		filter: (rows, columnIds, filterValue) => {
			if (filterValue === "all") return rows;
			return rows.filter((row) => JSON.stringify(row.values.approved) === filterValue);
		},
		Filter: ({ setFilter, column }) => (
			<select className="form-control" onChange={(event) => setFilter(column.id, event.target.value)} onClick={(e) => e.stopPropagation()}>
				<option value="all">All</option>
				<option value="true">Approved</option>
				<option value="false">Not Approved</option>
			</select>
		),
		filterable: true,
	},
	{
		id: "duration",
		Header: "Duration",
		accessor: (row) => {
			if (row.period === "HD") return "4.00h";
			if (row.period === "FD") return "8.00h";
			return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
		},
		sortable: true,
		filterable: true,
	},
	{
		id: "actions",
		Header: "actions",
		accessor: "actions",
		sortable: false,
		filterable: false,
	},
];

export const otTableColumns = [
	{
		id: "status",
		Header: "Status",
		accessor: (row) => (
			<div className={`${row.status === "Processed" ? "text-success" : "text-warning"}`}>
				{row.status === "Processed" ? <VscPassFilled size={20} /> : <MdPendingActions size={20} />}
			</div>
		),
		sortable: true,
		filterable: true,
	},
	{
		id: "date",
		Header: "Date",
		accessor: (row) => new Date(row.date).toDateString(),
		sortable: true,
		filterable: true,
	},
	{
		id: "duration",
		Header: "Duration",
		accessor: (row) => {
			if (row.period === "HD") return "4:00h";
			if (row.period === "FD") return "8:00h";
			return `${Math.min(8, absTimeDifference(row.startTime, row.endTime) / 60).toFixed(2)}h`;
		},
		sortable: true,
		filterable: true,
	},
	{
		id: "approved",
		Header: "Approved",
		accessor: (row) => (
			<div className={`${row.approved ? "text-success" : "text-danger"}`}>{row.approved ? <TiTick size={20} /> : <TiDelete size={20} />}</div>
		),
		sortable: true,
		filterable: true,
	},
	{
		id: "actions",
		Header: "actions",
		accessor: "actions",
		sortable: false,
		filterable: false,
	},
];

export const myLessonRecordsTableColumn = [
	{
		id: "lessonDate",
		Header: "Lesson Date",
		accessor: (row) => row.date,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonCentre",
		Header: "Centre",
		accessor: (row) => row.centre,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonStartTime",
		Header: "Lesson Start Time",
		accessor: (row) => row.startTime,
		sortable: true,
		filterable: true,
	},
	{
		id: "lessonEndTime",
		Header: "Lesson End Time",
		accessor: (row) => row.endTime,
		sortable: true,
		filterable: true,
	},
	{
		id: "studentName",
		Header: "Student Name",
		accessor: (row) => row.students.map((student) => student?.studentDetail?.firstName + " " + student.studentDetail?.lastName).join(", "),
		sortable: true,
		filterable: true,
	},
	{
		id: "studentReportAdded",
		Header: "Student Report",
		accessor: (row) =>
			row.students?.every((student) => student.studentReportAdded) ? <TiTick className="text-success" /> : <FiX className="text-danger" />,
		filterable: true,
	},
	{
		id: "studentReportApproved",
		Header: "Student Report Approved",
		accessor: (row) =>
			row.students?.every((student) => student.studentReportApproved) ? <TiTick className="text-success" /> : <FiX className="text-danger" />,
		filterable: true,
	},
	{
		id: "lessonCompleted",
		Header: "Lesson Completed",
		accessor: (row) =>
			row.students?.every((student) => student.lessonCompleted) ? <TiTick className="text-success" /> : <FiX className="text-danger" />,
		filterable: true,
	},
];

export const createStaffInitialData = {
	active: false,
	annualLeaveDaysCount: 0,
	birthday: "",
	compensationLeaveHoursCount: 0,
	email: "",
	employeeType: "",
	fullName: "",
	officialStartDate: "",
	region: "",
	sickLeaveDaysCount: "",
	status: "",
	unpaidLeaveDaysCount: "",
	username: "",
};
